import { useCallback, useRef, useState, type ChangeEvent, type FormEvent } from 'react'

export interface CheckboxInputProps {
  checked: boolean
  onChange: (e: ChangeEvent<HTMLInputElement> | boolean) => void
  error: string | undefined
}
export interface ValueInputProps<V> {
  value: V
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | V) => void
  error: string | undefined
}

type Errors<T> = Partial<Record<keyof T & string, string | null>>
type Validators<T> = Partial<{ [K in keyof T]: (value: T[K], values: T) => string | null | undefined }>

/**
 * Hafif form durumu. Uygulamadaki kullanımın alt kümesini sağlar:
 * `values`, `getInputProps(name, { type: 'checkbox' })`, `onSubmit`, `setFieldValue`,
 * `setValues`, `reset`, `isDirty`, `resetDirty`. Doğrulama gönderimde yapılır; hata
 * gösterildikten sonra alan değişince o alanın hatası silinir.
 */
export function useForm<T extends Record<string, unknown>>({
  initialValues, validate,
}: { initialValues: T; validate?: Validators<T> }) {
  const [values, setValuesState] = useState<T>(initialValues)
  const [errors, setErrors] = useState<Errors<T>>({})
  const baseline = useRef<T>(initialValues)

  const setFieldValue = useCallback(<K extends keyof T>(name: K, value: T[K]) => {
    setValuesState((v) => ({ ...v, [name]: value }))
    setErrors((e) => (e[name as keyof T & string] ? { ...e, [name]: null } : e))
  }, [])

  const setValues = useCallback((next: Partial<T> | ((prev: T) => Partial<T>)) => {
    setValuesState((v) => ({ ...v, ...(typeof next === 'function' ? next(v) : next) }))
  }, [])

  const runValidation = (v: T): Errors<T> => {
    const out: Errors<T> = {}
    if (!validate) return out
    for (const k of Object.keys(validate) as Array<keyof T & string>) {
      const fn = validate[k]
      const msg = fn?.(v[k], v)
      if (msg) out[k] = msg
    }
    return out
  }

  function getInputProps<K extends keyof T & string>(name: K, opts: { type: 'checkbox' }): CheckboxInputProps
  function getInputProps<K extends keyof T & string>(name: K): ValueInputProps<T[K]>
  function getInputProps<K extends keyof T & string>(name: K, opts?: { type?: 'checkbox' }): CheckboxInputProps | ValueInputProps<T[K]> {
    const value = values[name]
    const error = errors[name] ?? undefined
    if (opts?.type === 'checkbox') {
      return {
        checked: Boolean(value),
        onChange: (e: ChangeEvent<HTMLInputElement> | boolean) =>
          setFieldValue(name, (typeof e === 'boolean' ? e : e.currentTarget.checked) as T[K]),
        error,
      }
    }
    return {
      value: value as T[K],
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | T[K]) => {
        const v = e && typeof e === 'object' && 'currentTarget' in (e as object)
          ? (e as ChangeEvent<HTMLInputElement>).currentTarget.value
          : e
        setFieldValue(name, v as T[K])
      },
      error,
    }
  }

  const onSubmit = (handler: (values: T) => void) => (e?: FormEvent) => {
    e?.preventDefault()
    const found = runValidation(values)
    setErrors(found)
    if (Object.values(found).some(Boolean)) return
    handler(values)
  }

  const reset = useCallback(() => {
    setValuesState(baseline.current)
    setErrors({})
  }, [])

  const isDirty = () => JSON.stringify(values) !== JSON.stringify(baseline.current)
  const resetDirty = (v?: T) => { baseline.current = v ?? values }

  return { values, errors, setErrors, setFieldValue, setValues, getInputProps, onSubmit, reset, isDirty, resetDirty }
}
